// Frontend types for the user tier system

export enum UserTier {
  TIER_0 = 0,
  TIER_1 = 1,
  TIER_2 = 2,
  TIER_3 = 3,
  TIER_4 = 4,
  TIER_5 = 5,
  TIER_6 = 6,
  TIER_7 = 7,
}

export interface UserPreferences {
  language: string;
  timezone: string;
  notifications: {
    email: boolean;
    sms: boolean;
    push: boolean;
  };
  theme: 'light' | 'dark' | 'auto';
  botDefaults: {
    responseFormat: 'text' | 'markdown' | 'html';
    maxTokens: number;
    temperature: number;
  };
}

export interface BotConfiguration {
  id: string;
  name: string;
  type: 'chatbot' | 'assistant' | 'agent';
  model: string;
  systemPrompt: string;
  parameters: {
    temperature: number;
    maxTokens: number;
    topP: number;
  };
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IdentityData {
  documentType: 'passport' | 'drivers_license' | 'national_id';
  documentNumber: string;
  documentCountry: string;
  fullName: string;
  dateOfBirth: string;
  verified: boolean;
  verifiedAt?: string;
  verificationProvider: string;
  photoUrl?: string;
  documentImageUrls: string[];
}

export interface PaymentMethod {
  id: string;
  type: 'credit_card' | 'debit_card' | 'bank_account' | 'crypto_wallet';
  provider: string;
  last4?: string;
  expiryMonth?: number;
  expiryYear?: number;
  verified: boolean;
  default: boolean;
  createdAt: string;
}

export interface User {
  id: string;
  telegramUrl?: string;
  email?: string;
  phone?: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  preferences?: UserPreferences;
  bots?: BotConfiguration[];
  identityVerification?: IdentityData;
  paymentMethods?: PaymentMethod[];
  tier: UserTier;
  spendingTotal: number;
  spendingCurrentMonth: number;
  tierUpgradedAt: string;
  tierEligibleFor?: UserTier;
  organizationId?: string;
  role: 'user' | 'admin' | 'super_admin';
  status: 'active' | 'suspended' | 'deleted';
  metadata: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  loginCount: number;
}

export interface OrganizationSettings {
  billingEmail: string;
  allowedDomains: string[];
  ssoEnabled: boolean;
  maxUsers: number;
  features: {
    customBots: boolean;
    advancedAnalytics: boolean;
    prioritySupport: boolean;
    apiAccess: boolean;
  };
  spendingLimits: {
    monthly: number;
    perUser: number;
  };
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  adminUsers: string[];
  members: string[];
  settings: OrganizationSettings;
  tier: UserTier;
  spendingTotal: number;
  spendingCurrentMonth: number;
  status: 'active' | 'suspended' | 'deleted';
  createdAt: string;
  updatedAt: string;
}

export interface FeatureFlag {
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  tierRestriction?: UserTier;
  organizationRestriction?: string[];
  userRestriction?: string[];
  rolloutPercentage: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface TierProgressInfo {
  currentTier: UserTier;
  nextTier?: UserTier;
  progress: {
    [requirement: string]: boolean;
  };
  completionPercentage: number;
  missingRequirements: string[];
  eligibleForUpgrade: boolean;
}

export interface UserAnalytics {
  totalUsers: number;
  tierDistribution: {
    [tier: number]: number;
  };
  monthlyGrowth: {
    [month: string]: number;
  };
  verificationStats: {
    emailVerified: number;
    phoneVerified: number;
    identityVerified: number;
  };
  organizationStats: {
    totalOrganizations: number;
    averageUsersPerOrg: number;
  };
}

export interface TierUpgradeResponse {
  success: boolean;
  newTier: UserTier;
  message: string;
  requirements?: {
    [key: string]: boolean;
  };
}

// UI specific types
export interface TierBadgeProps {
  tier: UserTier;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export interface TierProgressBarProps {
  progressInfo: TierProgressInfo;
  showDetails?: boolean;
}

export interface UserPermissionMatrixProps {
  userId: string;
  canEdit?: boolean;
}

export interface AdminContextSwitcherProps {
  currentUser: User;
  onImpersonate: (userId: string) => void;
  onEndImpersonation: () => void;
}

export interface FeatureFlagToggleProps {
  flag: FeatureFlag;
  onToggle: (key: string, enabled: boolean) => void;
  disabled?: boolean;
}

// API Response types
export interface ApiResponse<T = any> {
  success?: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  cursor?: string;
  hasMore: boolean;
  total?: number;
}

// Form types
export interface CreateUserForm {
  telegramUrl?: string;
  email?: string;
  phone?: string;
  organizationId?: string;
}

export interface UpdateUserForm {
  email?: string;
  phone?: string;
  preferences?: Partial<UserPreferences>;
  metadata?: Record<string, any>;
}

export interface CreateOrganizationForm {
  name: string;
  slug: string;
  settings: OrganizationSettings;
}

export interface VerificationForm {
  type: 'email' | 'phone' | 'identity';
  code?: string;
  data?: any;
}

export interface IdentityVerificationForm {
  documentType: 'passport' | 'drivers_license' | 'national_id';
  documentNumber: string;
  documentCountry: string;
  fullName: string;
  dateOfBirth: string;
  documentImages: File[];
  photoImage?: File;
}

// Route definitions
export interface TierRoute {
  path: string;
  name: string;
  component: any;
  meta?: {
    requiresAuth?: boolean;
    requiresAdmin?: boolean;
    minimumTier?: UserTier;
    title?: string;
  };
}

// Constants
export const TIER_NAMES: Record<UserTier, string> = {
  [UserTier.TIER_0]: 'Basic Access',
  [UserTier.TIER_1]: 'Verified Contact',
  [UserTier.TIER_2]: 'Personalized Experience',
  [UserTier.TIER_3]: 'Multi-Bot Power User',
  [UserTier.TIER_4]: 'Identity Verified',
  [UserTier.TIER_5]: 'Payment Ready',
  [UserTier.TIER_6]: 'Proven Spender',
  [UserTier.TIER_7]: 'Enterprise Customer',
};

export const TIER_COLORS: Record<UserTier, string> = {
  [UserTier.TIER_0]: 'gray',
  [UserTier.TIER_1]: 'blue',
  [UserTier.TIER_2]: 'green',
  [UserTier.TIER_3]: 'yellow',
  [UserTier.TIER_4]: 'orange',
  [UserTier.TIER_5]: 'red',
  [UserTier.TIER_6]: 'purple',
  [UserTier.TIER_7]: 'pink',
};

export const TIER_DESCRIPTIONS: Record<UserTier, string> = {
  [UserTier.TIER_0]: 'Telegram URL only - basic entry level',
  [UserTier.TIER_1]: 'Email and phone verified for enhanced security',
  [UserTier.TIER_2]: 'User preferences configured for tailored experience',
  [UserTier.TIER_3]: 'Multiple bots configured for diverse workflows',
  [UserTier.TIER_4]: 'ID/Passport and photo verified for trusted access',
  [UserTier.TIER_5]: 'Credit card information added for premium services',
  [UserTier.TIER_6]: 'Spending threshold reached - trusted customer',
  [UserTier.TIER_7]: 'High-value customer with full access',
};
